import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { safeText } from '../lib/render';
import Wizard, { FloatingAction } from '../components/Wizard';

function fmtDate(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s.slice(0, 10);
  }
}

export default function Generate() {
  const nav = useNavigate();
  const [past, setPast] = useState([]);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [analysesLoaded, setAnalysesLoaded] = useState(false);
  const [language, setLanguage] = useState('en');
  const [languages, setLanguages] = useState([]);
  const [credits, setCredits] = useState(null);
  const [err, setErr] = useState(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [wizardErr, setWizardErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [langRes, me, wbRes, diss] = await Promise.all([
          api.get('/api/languages').catch(() => ({ languages: { en: 'English' } })),
          api.get('/api/users/me').catch(() => null),
          api.get('/api/worldbank/analyses').catch(() => ({ analyses: [] })),
          api.get('/api/dissertations').catch(() => ({ dissertations: [] })),
        ]);
        const raw = langRes.languages;
        let entries;
        if (Array.isArray(raw)) entries = raw.map((l) => [String(l), String(l)]);
        else if (raw && typeof raw === 'object') {
          entries = Object.entries(raw).map(([code, val]) =>
            val && typeof val === 'object' ? [code, String(val.name ?? val.label ?? code)]
                                           : [code, String(val ?? code)],
          );
        } else entries = [['en', 'English']];
        setLanguages(entries);
        if (langRes.default) setLanguage(langRes.default);
        if (me) setCredits(me.credits);

        const list = Array.isArray(wbRes.analyses) ? wbRes.analyses : [];
        const usable = list
          .filter((a) => a && String(a.status || '').toLowerCase() === 'complete'
            && Array.isArray(a.questions) && a.questions.length > 0)
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setAnalyses(usable);
        setAnalysesLoaded(true);

        const ds = diss.dissertations || diss || [];
        const pastList = Array.isArray(ds) ? ds : [];
        pastList.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setPast(pastList);
        setPastLoaded(true);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load');
        setLanguages([['en', 'English']]);
        setAnalysesLoaded(true);
        setPastLoaded(true);
      }
    })();
  }, []);

  async function submit(state) {
    if (!state.selectedKey) {
      setWizardErr('Pick a research topic.');
      return;
    }
    setWizardBusy(true);
    setWizardErr(null);
    try {
      const [aid, qi] = String(state.selectedKey).split('|');
      const a = analyses.find((x) => x.analysis_id === aid);
      if (!a) {
        setWizardErr('Selected topic no longer available.');
        return;
      }
      const question = (a.questions || [])[Number(qi)];
      const topic = typeof question === 'string' ? question : (question?.question || '');
      const researchArea =
        `${a.var1_name} and ${a.var2_name} in ${a.country_name || a.country_iso3}`;
      const res = await api.post('/api/dissertations', {
        topic,
        research_area: researchArea,
        language: state.language || language,
        use_queue: true,
        worldbank_analysis_id: a.analysis_id,
        worldbank_methodology: 'time_series',
        worldbank_var1_code: a.var1_code || '',
        worldbank_var2_code: a.var2_code || '',
        worldbank_country: a.country_iso3 || '',
        worldbank_start_year: a.start_year || '',
        worldbank_end_year: a.end_year || '',
      });
      if (res.dissertation_id) nav(`/dissertation/${res.dissertation_id}/live`);
      else if (res.task_id) nav('/dashboard');
      else setWizardErr(res.error || 'Generation started but no id returned.');
    } catch (e) {
      setWizardErr(e instanceof ApiError ? e.message : 'Failed to start generation');
    } finally {
      setWizardBusy(false);
    }
  }

  const noAnalyses = analysesLoaded && analyses.length === 0;

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4 text-center text-white">
          <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
            <i className="fas fa-graduation-cap me-1"></i>Dissertations
          </span>
          <h1 style={{ fontWeight: 800, fontSize: '2.6rem' }}>Your dissertations</h1>
          <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 640, margin: '0 auto' }}>
            Past work at a glance. Click <strong>New dissertation</strong> to start a new one
            — grounded in one of your time-series analyses.
          </p>
        </div>
      </section>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            {err && <div className="alert alert-danger">{err}</div>}

            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <h4 className="mb-0">
                <i className="fas fa-history me-2" style={{ color: 'var(--primary)' }}></i>
                Past dissertations
                {credits !== null && (
                  <span className="text-muted fw-normal small ms-3">
                    Balance: <strong>{credits} credits</strong>
                    {' · '}
                    <Link to="/pricing">Buy more</Link>
                  </span>
                )}
              </h4>
              <button
                type="button"
                className="btn btn-cta"
                onClick={() => {
                  setWizardErr(null);
                  if (noAnalyses) {
                    nav('/worldbank');
                    return;
                  }
                  setWizardOpen(true);
                }}
              >
                <i className="fas fa-plus me-1"></i>New dissertation
              </button>
            </div>

            {!pastLoaded ? (
              <div className="text-muted">Loading your dissertations…</div>
            ) : past.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-graduation-cap fa-3x text-muted mb-3"></i>
                  <h5>No dissertations yet</h5>
                  <p className="text-muted mb-3" style={{ maxWidth: 560, margin: '0 auto' }}>
                    {noAnalyses
                      ? <>You need a completed time-series analysis first — each
                         one generates 5 research topics you can pick from.</>
                      : <>Click <strong>New dissertation</strong> to start your first one.</>}
                  </p>
                  {noAnalyses ? (
                    <Link to="/worldbank" className="btn btn-cta btn-lg">
                      <i className="fas fa-flask me-1"></i>Run a time-series analysis
                    </Link>
                  ) : (
                    <button className="btn btn-cta btn-lg" onClick={() => setWizardOpen(true)}>
                      <i className="fas fa-plus me-1"></i>Start your first dissertation
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="list-group mb-4">
                {past.map((d) => (
                  <DissertationRow key={d.id} d={d} onResumed={() => window.location.reload()} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <FloatingAction
        label={noAnalyses ? 'Run a time-series first' : 'New dissertation'}
        icon={noAnalyses ? 'fa-flask' : 'fa-plus'}
        onClick={() => {
          if (noAnalyses) { nav('/worldbank'); return; }
          setWizardErr(null);
          setWizardOpen(true);
        }}
      />

      {wizardOpen && (
        <Wizard
          title="New dissertation"
          busy={wizardBusy}
          error={wizardErr}
          onClose={() => !wizardBusy && setWizardOpen(false)}
          onSubmit={submit}
          submitLabel="Generate (31 credits)"
          initialState={{ selectedKey: '', language, acknowledged: false }}
          steps={[
            {
              label: 'Pick topic',
              canAdvance: (s) => !!s.selectedKey,
              render: ({ state, setState }) => (
                <TopicPickStep state={state} setState={setState} analyses={analyses} />
              ),
            },
            {
              label: 'Language',
              canAdvance: (s) => !!s.language,
              render: ({ state, setState }) => (
                <LanguageStep state={state} setState={setState} languages={languages} />
              ),
            },
            {
              label: 'Review',
              canAdvance: (s) => !!s.acknowledged,
              render: ({ state, setState }) => (
                <DissertationReview
                  state={state}
                  setState={setState}
                  analyses={analyses}
                  languages={languages}
                />
              ),
            },
          ]}
        />
      )}
    </>
  );
}

function DissertationRow({ d, onResumed }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const status = String(d.status || '').toLowerCase();
  const failed = status === 'failed' || status === 'error';
  const done = status === 'completed' || status === 'complete';
  const to = done ? `/dissertation/${d.id}` : `/dissertation/${d.id}/live`;

  async function retry(e) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(`/api/dissertation/${d.id}/resume`, {});
      if (res?.success || res?.task_id || res?.dissertation_id || res?.message) {
        if (onResumed) onResumed();
        nav(`/dissertation/${d.id}/live`);
      } else {
        setErr(res?.error || 'Retry failed');
      }
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : 'Retry failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Link
      to={to}
      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-wrap gap-2"
      style={{ padding: '1rem' }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{safeText(d.topic, '(untitled)')}</div>
        <div className="text-muted small mt-1">
          <i className="fas fa-calendar-alt me-1"></i>
          {d.created_at ? fmtDate(d.created_at) : '—'}
        </div>
        {err && <div className="text-danger small mt-1">{err}</div>}
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className={`status-badge ${status || 'pending'}`}>{status || 'pending'}</span>
        {failed && (
          <button
            type="button"
            className="btn btn-sm btn-warning"
            onClick={retry}
            disabled={busy}
            title="Re-run generation — free, this one was on us"
          >
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-redo'} me-1`}></i>
            {busy ? 'Retrying…' : 'Retry (free)'}
          </button>
        )}
      </div>
    </Link>
  );
}

function TopicPickStep({ state, setState, analyses }) {
  return (
    <div>
      <h5 className="mb-2">Pick a research topic</h5>
      <p className="text-muted small mb-3">
        Topics are generated by your past time-series analyses. Pick one to
        ground the dissertation in real econometric evidence.
      </p>
      <div
        className="border rounded-3"
        style={{ maxHeight: 420, overflowY: 'auto', background: '#fff' }}
      >
        {analyses.map((a, gi) => (
          <div key={a.analysis_id}>
            <div
              className="px-3 py-2 small d-flex justify-content-between align-items-center flex-wrap gap-2"
              style={{
                background: '#f6f7fb',
                borderTop: gi === 0 ? 'none' : '1px solid var(--border)',
                borderBottom: '1px solid var(--border)',
                position: 'sticky', top: 0, zIndex: 1,
                fontWeight: 600, color: '#333',
              }}
            >
              <span>
                <i className="fas fa-flask text-primary me-2"></i>
                {a.var1_name} <span className="text-muted fw-normal">vs</span> {a.var2_name}
                <span className="text-muted fw-normal"> · {a.country_name || a.country_iso3} ({a.start_year}–{a.end_year})</span>
              </span>
            </div>
            {(a.questions || []).map((q, i) => {
              const key = `${a.analysis_id}|${i}`;
              const text = typeof q === 'string' ? q : (q.question || '');
              const rationale = typeof q === 'object' ? (q.rationale || '') : '';
              const active = state.selectedKey === key;
              return (
                <label
                  key={key}
                  className="d-flex gap-3 px-3 py-3 m-0"
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: active ? 'rgba(79, 140, 255, 0.08)' : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="topic-pick"
                    className="form-check-input mt-1"
                    checked={active}
                    onChange={() => setState({ ...state, selectedKey: key })}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ lineHeight: 1.4 }}>
                      <span className="text-muted me-2 small">{i + 1}.</span>
                      {text}
                    </div>
                    {rationale && (
                      <div className="text-muted small mt-1">{rationale}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function LanguageStep({ state, setState, languages }) {
  return (
    <div>
      <h5 className="mb-2">Pick a language</h5>
      <p className="text-muted small mb-3">
        The whole dissertation (all 5 chapters) will be written in this language.
      </p>
      <select
        className="form-select form-select-lg"
        value={state.language}
        onChange={(e) => setState({ ...state, language: e.target.value })}
      >
        {languages.map(([code, name]) => (
          <option key={code} value={code}>{name}</option>
        ))}
      </select>
    </div>
  );
}

function DissertationReview({ state, setState, analyses, languages }) {
  const [aid, qi] = String(state.selectedKey || '').split('|');
  const a = analyses.find((x) => x.analysis_id === aid);
  const q = a ? (a.questions || [])[Number(qi)] : null;
  const topic = typeof q === 'string' ? q : (q?.question || '');
  const lang = (languages.find(([c]) => c === state.language) || [])[1] || state.language;
  return (
    <div>
      <h5 className="mb-3">Ready to generate</h5>
      <div className="card" style={{ background: '#fafbfd', border: '1px solid var(--border)' }}>
        <div className="card-body">
          <Row label="Research topic" value={topic} wrap />
          {a && (
            <Row
              label="Grounded in"
              value={`${a.var1_name} vs ${a.var2_name} — ${a.country_name || a.country_iso3} (${a.start_year}–${a.end_year})`}
              wrap
            />
          )}
          <Row label="Language" value={lang} />
        </div>
      </div>
      <div className="alert alert-info small mt-3 mb-0">
        <i className="fas fa-info-circle me-2"></i>
        Dissertation generation runs 1–3 hours in the background. You can close
        the tab — it continues server-side and resumes from checkpoint if a
        worker is evicted mid-run.
      </div>

      <div
        className="mt-3 p-3"
        style={{
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: 10,
          color: '#92400e',
        }}
      >
        <div className="fw-semibold mb-2" style={{ color: '#78350f' }}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          Before you generate — please read
        </div>
        <p className="small mb-2" style={{ lineHeight: 1.55 }}>
          What you receive is a <strong>complete first draft</strong>, not a finished
          submission. You should expect to:
        </p>
        <ul className="small mb-2" style={{ lineHeight: 1.55, paddingLeft: '1.2rem' }}>
          <li>Read the entire document end-to-end and edit any section that does not match your study, voice, or supervisor's guidance.</li>
          <li>Verify every citation — confirm the source exists and that the claim attributed to it is accurate.</li>
          <li>Re-check the data and statistical results against the underlying public dataset.</li>
          <li>Adapt generic sections (introduction, ethics, methodology, conclusion) to your institution and country context.</li>
          <li>Disclose AI-assisted writing if your institution requires it.</li>
        </ul>
        <p className="small mb-0" style={{ lineHeight: 1.55 }}>
          Final responsibility for accuracy, originality, and submission-readiness lies with
          you. See the{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">
            Terms and Conditions
          </Link>{' '}
          for the full statement.
        </p>
      </div>

      <div
        className="form-check mt-3"
        style={{
          fontSize: '0.875rem',
          padding: '0.75rem 0.75rem 0.75rem 2.25rem',
          background: '#f8fafc',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <input
          type="checkbox"
          className="form-check-input"
          id="generateAckCheckbox"
          checked={!!state.acknowledged}
          onChange={(e) => setState((s) => ({ ...s, acknowledged: e.target.checked }))}
        />
        <label className="form-check-label" htmlFor="generateAckCheckbox">
          I understand this is a draft and I am responsible for reviewing, editing,
          verifying citations and data, and adapting the document before submission.
        </label>
      </div>
    </div>
  );
}

function Row({ label, value, wrap }) {
  return (
    <div className="d-flex gap-3 py-1" style={{ borderBottom: '1px dashed rgba(0,0,0,0.06)' }}>
      <div className="text-muted small" style={{ minWidth: 130 }}>{label}</div>
      <div
        className="small"
        style={{
          flex: 1,
          wordBreak: wrap ? 'break-word' : 'normal',
          whiteSpace: wrap ? 'normal' : 'nowrap',
          fontWeight: 500,
        }}
      >
        {value || <span className="text-muted">—</span>}
      </div>
    </div>
  );
}
